import type { ComponentProps } from "react";

import type { ComposerThreadTarget } from "../../composerDraftStore";
import type { DraftId } from "../../draftId";
import { shortcutLabelForCommand } from "../../keybindings";
import { withComposerStashDefault } from "../../composerStashKeybindings";
import { useServerKeybindings } from "../../rpc/serverState";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import type { ComposerStashControlProps } from "./ComposerStashMenu";
import { ComposerSurface } from "./ComposerSurface";
import { useComposerStashMenu, type ComposerStashNoticeType } from "./useComposerStashController";

type ComposerStashTarget = ComposerThreadTarget | DraftId;
type ComposerStashSurfaceProps = Pick<
  ComponentProps<typeof ComposerSurface>,
  "keybindings" | "onStashShortcut" | "terminalOpen"
>;

export function useComposerStashIntegration(
  target: ComposerStashTarget,
  disabled: boolean,
  onNotice: (message: string, type: ComposerStashNoticeType) => void,
) {
  const keybindings = withComposerStashDefault(useServerKeybindings());
  const terminalOpen = useTerminalStateStore((state) =>
    typeof target === "string"
      ? false
      : selectThreadTerminalState(state.terminalStateByThreadKey, target).terminalOpen,
  );
  const stash = useComposerStashMenu({ target, onNotice });
  const shortcutLabel = shortcutLabelForCommand(keybindings, "composer.stash", {
    context: { terminalFocus: false, terminalOpen },
  });
  return {
    footer: {
      disabled,
      shortcutLabel,
      onResult: onNotice,
      adapter: stash.controller,
    } satisfies ComposerStashControlProps,
    surface: {
      keybindings,
      terminalOpen,
      onStashShortcut: disabled ? undefined : stash.controller.save,
    } satisfies ComposerStashSurfaceProps,
  };
}
